import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from 'react-bootstrap';
import {
    addSource,
    removeSource,
    setSourcePath,
    addTarget,
    removeTarget,
    setAnalysisResult,
    setMigrationLogs,
    addMigrationLog,
    setMigrationProgress,
    setIsAnalyzing,
    setIsMigrating,
    setCancelRequested,
    setErrors,
    addError,
    resetMigration,
} from '../../redux/slices/migrationSlice';
import ProgressBarWithStop from "../ProgressBarWithStop.js";
import { ReactComponent as AnalyzeFilesIcon } from '../../assets/icons/analyze-files.svg';
import { ReactComponent as EditFilenamesIcon } from '../../assets/icons/edit-filenames.svg';
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh.svg';
import { ReactComponent as DirectoryIcon } from '../../assets/icons/select-folder.svg';
import { useToast } from '../../contexts/ToastContext';
import { track as trackEvent } from '../../utils/analytics';
import rekordboxLogo from '../../assets/rekordbox-logo.png';
import seratoLogo from '../../assets/serato-logo-10px.png';
import traktorLogo from '../../assets/traktor-logo.png';
import virtualdjLogo from '../../assets/virtual-dj-logo.png';
import './MigrateLibraryTab.scss';
import { errMsg } from '../../redux/utils/errMsg';
import { useDesignV2 } from '../../utils/designV2';
import ToolHeader from '../v2/ToolHeader';

const PLATFORMS = [
    { key: 'rekordbox', name: 'Rekordbox', logo: rekordboxLogo },
    { key: 'serato', name: 'Serato', logo: seratoLogo },
    { key: 'traktor', name: 'Traktor', logo: traktorLogo },
    { key: 'virtualdj', name: 'VirtualDJ', logo: virtualdjLogo },
];

const MigrateLibraryTab = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    // DESIGN V2 — one header row (From → To chips, path, actions), a stats
    // sentence, sentence-case panels. Gated; the old structure is untouched.
    const v2 = useDesignV2();

    // Auto-detected platform installations
    const [detectedPlatforms, setDetectedPlatforms] = React.useState({});
    const [isDetecting, setIsDetecting] = React.useState(true);
    const [migrationResults, setMigrationResults] = React.useState(null); // { success, targetResults }
    const [analysisStatus, setAnalysisStatus] = React.useState(''); // Progress message during analysis
    const [selectedPlaylists, setSelectedPlaylists] = React.useState(new Set()); // Selected playlist names
    const [includeUncrated, setIncludeUncrated] = React.useState(true); // Include tracks not in any playlist

    const {
        sources,
        targets,
        outputDir,
        analysisResult,
        migrationProgress,
        isAnalyzing,
        isMigrating,
        migrationLogs,
        errors,
    } = useSelector((state) => state.migration);

    const isProcessing = isAnalyzing || isMigrating;
    const isSourceSelected = (key) => sources.some(s => s.platform === key);
    const isTargetSelected = (key) => targets.includes(key);
    const hasAnySources = sources.length > 0 && sources.some(s => s.path);
    const hasAnyTargets = targets.length > 0;
    const canAnalyze = hasAnySources && hasAnyTargets && !isProcessing;
    const canMigrate = analysisResult !== null && !isProcessing;

    // Auto-detect all platform installations on mount
    React.useEffect(() => {
        const detectPlatforms = async () => {
            const detected = {};

            try {
                detected.rekordbox = await window.electron.rekordbox.checkXmlExists();
            } catch (e) {
                detected.rekordbox = { exists: false, path: null };
            }

            try {
                detected.serato = await window.electron.serato.checkExists();
            } catch (e) {
                detected.serato = { exists: false, path: null };
            }

            try {
                detected.traktor = await window.electron.traktor.checkExists();
            } catch (e) {
                detected.traktor = { exists: false, path: null };
            }

            try {
                detected.virtualdj = await window.electron.virtualdj.checkExists();
            } catch (e) {
                detected.virtualdj = { exists: false, path: null };
            }

            setDetectedPlatforms(detected);
            setIsDetecting(false);
        };

        detectPlatforms();
    }, []);

    const handleSourceToggle = (platformKey) => {
        if (isSourceSelected(platformKey)) {
            dispatch(removeSource(platformKey));
        } else {
            // Auto-set path from detected platform
            const detected = detectedPlatforms[platformKey];
            let autoPath = (detected && detected.exists) ? (detected.path || '') : '';

            // SeratoParser expects the _Serato_ directory, but checkExists() returns
            // the database V2 file path. Normalize to the parent directory.
            if (platformKey === 'serato' && autoPath && !autoPath.endsWith('_Serato_')) {
                const parts = autoPath.replace(/\\/g, '/').split('/');
                const seratoIdx = parts.findIndex(p => p === '_Serato_');
                if (seratoIdx >= 0) {
                    autoPath = parts.slice(0, seratoIdx + 1).join('/');
                }
            }

            dispatch(addSource({ platform: platformKey, path: autoPath }));
            trackEvent('files_selected', { count: sources.length + 1, action: 'migrate' });
            // Auto-remove from targets if it was selected there
            if (isTargetSelected(platformKey)) {
                dispatch(removeTarget(platformKey));
            }
        }
    };

    const handleTargetToggle = (platformKey) => {
        if (isTargetSelected(platformKey)) {
            dispatch(removeTarget(platformKey));
        } else {
            dispatch(addTarget(platformKey));
            trackEvent('migration_target_selected', { source: sources[0]?.platform || null, target: platformKey });
        }
    };

    const handleSelectSourcePath = async (platformKey) => {
        if (platformKey === 'rekordbox') {
            const result = await window.electron.dialog.showOpenDialog({
                properties: ['openFile'],
                filters: [{ name: 'Rekordbox Files', extensions: ['db', 'xml'] }],
            });
            if (result && !result.canceled && result.filePaths && result.filePaths.length > 0) {
                dispatch(setSourcePath({ platform: platformKey, path: result.filePaths[0] }));
            }
        } else {
            const chosenDir = await window.electron.openDir();
            if (chosenDir && chosenDir.length > 0) {
                dispatch(setSourcePath({ platform: platformKey, path: chosenDir[0] }));
            }
        }
    };

    const handleStopProcessing = () => {
        dispatch(setCancelRequested(true));
        dispatch(setIsAnalyzing(false));
        dispatch(setIsMigrating(false));
        try {
            if (window.electron && window.electron.migration && window.electron.migration.cancel) {
                window.electron.migration.cancel();
            }
        } catch (e) {
            console.error('Error cancelling migration:', e);
        }
    };

    const handleAnalyze = () => {
        dispatch(setIsAnalyzing(true));
        dispatch(setMigrationProgress(5));
        dispatch(setMigrationLogs([]));
        dispatch(setErrors([]));
        dispatch(setAnalysisResult(null));
        setAnalysisStatus('Reading library...');

        if (window.electron && window.electron.migration && window.electron.migration.analyze) {
            let liveResult = null;
            const wrappedHandleProgress = ({ stage, progress, message, partialResult, scannedCount, totalCount, enrichedCount, totalCues, totalLoops }) => {
                dispatch(setMigrationProgress(progress));
                if (stage === 'parsing') {
                    setAnalysisStatus(message);
                } else if (stage === 'partial-result' && partialResult) {
                    // Show tracks/playlists immediately (with 0 cues)
                    liveResult = partialResult;
                    dispatch(setAnalysisResult(partialResult));
                    // Select all playlists by default
                    setSelectedPlaylists(new Set((partialResult.playlists || []).map((pl, i) => playlistKey(pl, i))));
                    setIncludeUncrated(true);
                    setAnalysisStatus('Scanning cues & loops...');
                } else if (stage === 'enriching' && liveResult) {
                    setAnalysisStatus(`Scanning cues & loops: ${scannedCount}/${totalCount} tracks (${enrichedCount} with cues)`);
                    // Update cue/loop counters live
                    const updated = {
                        ...liveResult,
                        totalCues: totalCues || 0,
                        totalLoops: totalLoops || 0,
                        totalCuePoints: (totalCues || 0) + (totalLoops || 0),
                    };
                    dispatch(setAnalysisResult(updated));
                    liveResult = updated;
                }
            };

            const handleComplete = (result) => {
                dispatch(setMigrationProgress(100));
                dispatch(setAnalysisResult(result));
                // Ensure all playlists are selected after final result
                setSelectedPlaylists(new Set((result.playlists || []).map((pl, i) => playlistKey(pl, i))));
                setIncludeUncrated(true);
                setAnalysisStatus('');
                liveResult = null;
                if (result.errors && result.errors.length > 0) {
                    result.errors.forEach(err => dispatch(addError(err)));
                }
                dispatch(setIsAnalyzing(false));
            };

            window.electron.migration.analyze({ sources, targets }, wrappedHandleProgress, handleComplete);
        } else {
            dispatch(setMigrationProgress(100));
            dispatch(setAnalysisResult({
                totalTracks: 0,
                totalPlaylists: 0,
                totalCuePoints: 0,
                sources: [],
                targets: [],
                errors: ['Migration API not available. Restart the app to load the migration backend.'],
            }));
            dispatch(addError('Migration API not available. Restart the app to load the migration backend.'));
            dispatch(setIsAnalyzing(false));
        }
    };

    const handleMigrate = async () => {
        // Pre-flight: check if any source or target apps are running
        const platformNames = { rekordbox: 'Rekordbox', serato: 'Serato', traktor: 'Traktor', virtualdj: 'VirtualDJ' };
        try {
            const sourcePlatforms = sources.map(s => s.platform);
            const allPlatforms = [...new Set([...sourcePlatforms, ...targets])];
            const running = await window.electron.migration.checkRunningApps(allPlatforms);
            const names = (list) => {
                const l = list.map(p => platformNames[p] || p);
                return l.length > 1 ? `${l.slice(0, -1).join(', ')} and ${l[l.length - 1]}` : l[0];
            };
            // A running TARGET rewrites its library on exit (Rekordbox also locks
            // its DB) — whatever we write is lost, so that's a hard stop with the
            // same wording every other DJ-library write uses. A running SOURCE
            // only risks unsaved edits not being read yet — warn, allow.
            const runningTargets = (running || []).filter(p => targets.includes(p));
            const runningSources = (running || []).filter(p => !targets.includes(p));
            if (runningTargets.length > 0) {
                const plural = runningTargets.length > 1;
                toast.error(`${names(runningTargets)} ${plural ? 'are' : 'is'} open — close ${plural ? 'them' : 'it'} and run the migration again.`, 9000);
                return;
            }
            if (runningSources.length > 0) {
                const plural = runningSources.length > 1;
                const confirmed = await toast.confirm(
                    `${names(runningSources)} ${plural ? 'are' : 'is'} open. Changes you haven’t saved there yet won’t be included.`,
                    `Close ${names(runningSources)} first?`,
                    { confirmText: 'Migrate anyway', cancelText: 'Cancel', confirmVariant: 'primary' }
                );
                if (!confirmed) return;
            }
        } catch (e) {
            console.warn('Failed to check running apps:', e);
            // Continue anyway if the check fails
        }

        window.electron.analytics.track('migration_start', {
            sources: sources.map(s => s.platform || s.type),
            targets: targets.map(t => t.platform || t.type),
        });
        dispatch(setIsMigrating(true));
        dispatch(setMigrationProgress(0));
        dispatch(setMigrationLogs([]));
        dispatch(setErrors([]));
        setMigrationResults(null);

        const handleProgress = ({ stage, progress, message, trackInfo }) => {
            dispatch(setMigrationProgress(progress));
            if (stage === 'track-migrated' && trackInfo) {
                dispatch(addMigrationLog({
                    filePath: trackInfo.filePath,
                    trackName: trackInfo.title || trackInfo.filePath,
                    artist: trackInfo.artist || '',
                    status: trackInfo.status || 'success',
                    message: trackInfo.target,
                }));
            }
        };

        const handleComplete = ({ success, targetResults, errors: completeErrors }) => {
            window.electron.analytics.track('migration_complete', { success });
            dispatch(setMigrationProgress(100));
            setMigrationResults({ success, targetResults: targetResults || [] });
            if (targetResults && targetResults.length > 0) {
                targetResults.forEach(result => {
                    if (result.warningMessages && result.warningMessages.length > 0) {
                        result.warningMessages.forEach(msg => dispatch(addError(msg)));
                    }
                    if (result.errors && result.errors.length > 0) {
                        result.errors.forEach(err => dispatch(addError(err)));
                    }
                });
            }
            if (completeErrors && completeErrors.length > 0) {
                completeErrors.forEach(err => dispatch(addError(err)));
            }
            dispatch(setIsMigrating(false));
        };

        try {
            if (window.electron && window.electron.migration && window.electron.migration.migrate) {
                window.electron.migration.migrate(
                    { sources, targets, outputDir, selectedPlaylists: [...selectedPlaylists].map(k => k.replace(/^\d+::/, '')), includeUncrated },
                    handleProgress,
                    handleComplete
                );
            } else {
                dispatch(addError('Migration API not available. Restart the app to load the migration backend.'));
                dispatch(setIsMigrating(false));
            }
        } catch (err) {
            dispatch(addError(errMsg(err)));
            dispatch(setIsMigrating(false));
        }
    };

    const formatPathDisplay = (p, segments = 2) => {
        if (!p) return '';
        const parts = p.replace(/\\/g, '/').split('/');
        if (parts.length <= segments + 1) return p;
        return '.../' + parts.slice(-segments).join('/');
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'success': return '\u2713';
            case 'warning': return '!';
            case 'error': return '\u2717';
            default: return '\u2022';
        }
    };

    const handleOpenOutputFolder = (outputPath) => {
        try {
            if (window.electron && window.electron.shell) {
                window.electron.shell.showItemInFolder(outputPath);
            }
        } catch (e) {
            console.error('Error opening folder:', e);
        }
    };

    const getImportInstructions = (platform, method) => {
        switch (platform) {
            case 'rekordbox':
                return 'Rekordbox \u2192 Preferences \u2192 Advanced \u2192 Database \u2192 enable "rekordbox xml"';
            case 'serato':
                return 'Done! Open Serato to see your imported tracks and crates.';
            case 'traktor':
                return method === 'database'
                    ? 'Done! Open Traktor to see your imported tracks and playlists.'
                    : 'Traktor \u2192 Preferences \u2192 File Management \u2192 import collection.nml';
            case 'virtualdj':
                return 'Done! Open VirtualDJ to see your tracks. Cues load when tracks are first played.';
            default:
                return '';
        }
    };

    // Playlist selection helpers
    const togglePlaylist = (name) => {
        setSelectedPlaylists(prev => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    };

    // Use index-based keys to handle duplicate playlist names
    const playlistKey = (pl, idx) => `${idx}::${pl.name}`;

    const allPlaylistKeys = React.useMemo(() => {
        if (!analysisResult?.playlists) return [];
        return analysisResult.playlists.map((pl, i) => playlistKey(pl, i));
    }, [analysisResult]);

    const hasUncrated = React.useMemo(() => {
        if (!analysisResult?.tracks || !analysisResult?.playlists) return false;
        const playlistPaths = new Set();
        analysisResult.playlists.forEach(pl => pl.tracks.forEach(t => playlistPaths.add(t.filePath)));
        return analysisResult.tracks.some(t => !playlistPaths.has(t.filePath));
    }, [analysisResult]);

    const noneDeselected = allPlaylistKeys.length > 0 && allPlaylistKeys.every(key => selectedPlaylists.has(key));
    const allSelected = noneDeselected && (!hasUncrated || includeUncrated);

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelectedPlaylists(new Set());
            setIncludeUncrated(false);
        } else {
            setSelectedPlaylists(new Set(allPlaylistKeys));
            setIncludeUncrated(true);
        }
    };

    // Compute selected track count for the Migrate button
    const selectedTrackCount = React.useMemo(() => {
        if (!analysisResult) return 0;
        const selectedPaths = new Set();
        (analysisResult.playlists || []).forEach((pl, i) => {
            if (selectedPlaylists.has(playlistKey(pl, i))) {
                pl.tracks.forEach(t => selectedPaths.add(t.filePath));
            }
        });
        if (includeUncrated) {
            const playlistPaths = new Set();
            (analysisResult.playlists || []).forEach(pl => pl.tracks.forEach(t => playlistPaths.add(t.filePath)));
            (analysisResult.tracks || []).forEach(t => {
                if (!playlistPaths.has(t.filePath)) selectedPaths.add(t.filePath);
            });
        }
        return selectedPaths.size;
    }, [analysisResult, selectedPlaylists, includeUncrated]);

    const hasStarted = isAnalyzing || isMigrating || analysisResult !== null || migrationLogs.length > 0;

    // ---- DESIGN V2 header + sentences ----
    const platformName = (key) => (PLATFORMS.find((p) => p.key === key) || {}).name || key;
    const sourceNames = sources.map((s) => platformName(s.platform)).join(' + ');
    const targetNames = targets.map(platformName).join(', ');
    const migrateDisabled = !canMigrate || selectedTrackCount === 0;
    const migrateTitle = isProcessing ? '' : !analysisResult ? 'Analyze the source library first' : selectedTrackCount === 0 ? 'Select at least one playlist or the uncrated tracks' : '';
    const analyzeTitle = !hasAnySources ? 'Pick a From platform (and its library path)' : !hasAnyTargets ? 'Pick a To platform' : '';
    const migratedOk = migrationLogs.filter((l) => l.status === 'success' || !l.status).length;
    const migratedSkipped = migrationLogs.length - migratedOk;
    const handleReset = () => { dispatch(resetMigration()); setMigrationResults(null); setSelectedPlaylists(new Set()); setIncludeUncrated(true); };
    const platformChip = (platform, side) => {
        const detected = detectedPlatforms[platform.key];
        const isAvailable = detected && detected.exists;
        const isSource = isSourceSelected(platform.key);
        const on = side === 'from' ? isSource : isTargetSelected(platform.key);
        const blocked = side === 'to' && isSource;
        const title = side === 'from'
            ? (isAvailable ? detected.path : (isDetecting ? 'Detecting…' : 'Not detected on this computer — you can still browse to a library'))
            : (blocked ? "Can't migrate to the platform you're migrating from" : `Write to ${platform.name}`);
        return (
            <button
                key={`${side}-${platform.key}`}
                type="button"
                className={`v2-chip sm${on ? ' on' : ''}${blocked ? ' v2-disabled' : ''}`}
                title={title}
                aria-pressed={on}
                disabled={isProcessing}
                onClick={() => { if (blocked) return; if (side === 'from') handleSourceToggle(platform.key); else handleTargetToggle(platform.key); }}
            >
                <img src={platform.logo} alt="" className="v2-chip-logo" />
                {platform.name}
                {side === 'from' && isAvailable && <span className="v2-dot ok" title="Detected" />}
            </button>
        );
    };
    const v2Header = v2 && (
        <ToolHeader
            className="v2-tool-migrate"
            source={(
                <div className="v2-migrate-chips">
                    <div className="v2-migrate-row"><span className="v2-faint v2-migrate-label">From</span>{PLATFORMS.map((p) => platformChip(p, 'from'))}</div>
                    <div className="v2-migrate-row"><span className="v2-faint v2-migrate-label">To</span>{PLATFORMS.map((p) => platformChip(p, 'to'))}</div>
                </div>
            )}
            stats={isProcessing ? (
                <ProgressBarWithStop isProcessing={isProcessing} progress={migrationProgress} handleStopProcessing={handleStopProcessing} />
            ) : sources.length > 0 ? (
                <span className="v2-migrate-paths">
                    {sources.map((source) => {
                        const platform = PLATFORMS.find((p) => p.key === source.platform);
                        if (!platform) return null;
                        return (
                            <button key={source.platform} type="button" className="v2-btn quiet sm v2-migrate-path" title={source.path ? `${source.path} — click to change` : `Pick the ${platform.name} library`} onClick={() => handleSelectSourcePath(source.platform)}>
                                {source.path ? formatPathDisplay(source.path) : `Browse for ${platform.name}…`}
                            </button>
                        );
                    })}
                </span>
            ) : null}
            actions={(
                <>
                    {hasStarted && !isProcessing && (
                        <button type="button" className="v2-btn quiet" onClick={handleReset}>Reset</button>
                    )}
                    {analysisResult ? (
                        <>
                            <button type="button" className="v2-btn analyze-button" onClick={handleAnalyze} disabled={!canAnalyze} title={analyzeTitle}>Analyze again</button>
                            <button type="button" className="v2-btn primary" aria-disabled={migrateDisabled ? 'true' : undefined} title={migrateTitle}
                                onClick={() => { if (migrateDisabled) return; handleMigrate(); }}>
                                Migrate{selectedTrackCount > 0 ? ` ${selectedTrackCount} ${selectedTrackCount === 1 ? 'track' : 'tracks'}` : ''}
                            </button>
                        </>
                    ) : (
                        <button type="button" className="v2-btn primary analyze-button" aria-disabled={!canAnalyze ? 'true' : undefined} title={analyzeTitle}
                            onClick={() => { if (!canAnalyze) return; handleAnalyze(); }}>Analyze</button>
                    )}
                </>
            )}
        />
    );
    const v2Sentence = v2 && (
        <div className="v2-migrate-sentence v2-stat">
            {analysisResult ? (
                <>
                    {sourceNames || 'Source'} library: <b>{analysisResult.totalTracks}</b> tracks · <b>{analysisResult.totalPlaylists}</b> playlists · <b>{analysisResult.totalCues || analysisResult.totalCuePoints || 0}</b> cues · <b>{analysisResult.totalLoops || 0}</b> loops.
                    {' '}<b>{selectedTrackCount}</b> selected.
                    {isAnalyzing && analysisStatus && <span className="v2-faint"> · {analysisStatus}</span>}
                </>
            ) : isAnalyzing ? (
                <span className="v2-faint">{analysisStatus || 'Analyzing libraries…'}</span>
            ) : (
                <span className="v2-faint">{sources.length === 0 ? 'Pick where your library lives today, and where it should go.' : !hasAnyTargets ? 'Pick a To platform, then Analyze.' : 'Analyze reads the source library — nothing is written until you migrate.'}</span>
            )}
        </div>
    );

    return (
        <div className={`tab-container migrate-library-tab${v2 ? ' v2-tool-page' : ''}`}>
            {v2Header}
            {v2Sentence}
            {!v2 && <div className="upper-part">
                {/* Platform selection: FROM and TO side by side */}
                <div className="platform-selection-row">
                    <div className="platform-section">
                        <span className="section-label">From</span>
                        <div className="platform-checkboxes">
                            {PLATFORMS.map(platform => {
                                const detected = detectedPlatforms[platform.key];
                                const isAvailable = detected && detected.exists;
                                return (
                                    <label
                                        key={platform.key}
                                        className={`platform-checkbox ${isSourceSelected(platform.key) ? 'selected' : ''} ${!isAvailable && !isDetecting ? 'unavailable' : ''}`}
                                        title={isAvailable ? detected.path : (isDetecting ? 'Detecting...' : 'Not detected')}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSourceSelected(platform.key)}
                                            onChange={() => handleSourceToggle(platform.key)}
                                            disabled={isProcessing}
                                        />
                                        <img src={platform.logo} alt={platform.name} className="platform-logo" />
                                        <span className="platform-name">{platform.name}</span>
                                        {isAvailable && <span className="platform-detected-dot" />}
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    <span className="platform-direction-arrow" aria-hidden="true">→</span>

                    <div className="platform-section">
                        <span className="section-label">To</span>
                        <div className="platform-checkboxes">
                            {PLATFORMS.map(platform => {
                                const isSource = isSourceSelected(platform.key);
                                return (
                                    <label
                                        key={platform.key}
                                        className={`platform-checkbox ${isTargetSelected(platform.key) ? 'selected' : ''} ${isSource ? 'unavailable' : ''}`}
                                        title={isSource ? "Can't migrate to the same platform you're migrating from" : ''}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isTargetSelected(platform.key)}
                                            onChange={() => handleTargetToggle(platform.key)}
                                            disabled={isProcessing || isSource}
                                        />
                                        <img src={platform.logo} alt={platform.name} className="platform-logo" />
                                        <span className="platform-name">{platform.name}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                </div>

            </div>}

            <div className="lower-part">
                <div className="log-container">
                    <div className="files-container comparison-view">
                        {/* Grounded source bar: source paths on the left, migration
                            actions on the right (progress swaps in while running). */}
                        {!v2 && <div className="upper-part source-bar-header migrate-source-bar">
                            <div className="source-bar">
                                <div className="source-bar-left">
                                    {isProcessing ? (
                                        <ProgressBarWithStop
                                            isProcessing={isProcessing}
                                            progress={migrationProgress}
                                            handleStopProcessing={handleStopProcessing}
                                        />
                                    ) : sources.length > 0 ? (
                                        <div className="source-paths-summary">
                                            {sources.map(source => {
                                                const platform = PLATFORMS.find(p => p.key === source.platform);
                                                if (!platform) return null;
                                                return (
                                                    <div key={source.platform} className="source-path-item">
                                                        <img src={platform.logo} alt={platform.name} className="source-path-logo" />
                                                        {source.path ? (
                                                            <span
                                                                className="source-path-text"
                                                                onClick={() => handleSelectSourcePath(source.platform)}
                                                                title={source.path}
                                                            >
                                                                {formatPathDisplay(source.path)}
                                                            </span>
                                                        ) : (
                                                            <span
                                                                className="source-path-placeholder"
                                                                onClick={() => handleSelectSourcePath(source.platform)}
                                                            >
                                                                Browse...
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <>
                                            <DirectoryIcon className="source-bar-icon" />
                                            <span className="source-bar-title empty">Select a source and target platform above to begin</span>
                                        </>
                                    )}
                                </div>
                                <div className="source-bar-actions">
                                    {hasStarted && !isProcessing && (
                                        <Button
                                            className="button choose-button"
                                            onClick={() => { dispatch(resetMigration()); setMigrationResults(null); setSelectedPlaylists(new Set()); setIncludeUncrated(true); }}
                                        >
                                            <RefreshIcon className="icon" />
                                            Reset
                                        </Button>
                                    )}
                                    <Button
                                        className="button analyze-button main-button"
                                        onClick={handleAnalyze}
                                        disabled={!canAnalyze}
                                    >
                                        <AnalyzeFilesIcon className="icon" />
                                        Analyze
                                    </Button>
                                    <Button
                                        className="button main-button"
                                        onClick={handleMigrate}
                                        disabled={!canMigrate || selectedTrackCount === 0}
                                    >
                                        <EditFilenamesIcon className="icon" />
                                        {canMigrate && selectedTrackCount > 0 && selectedTrackCount < (analysisResult?.totalTracks || 0)
                                            ? `Migrate (${selectedTrackCount})`
                                            : 'Migrate'}
                                    </Button>
                                </div>
                            </div>
                        </div>}

                        {/* Two-column comparison layout */}
                        <div className="comparison-container">
                            {/* Left column: Source libraries analysis */}
                            <div className="comparison-column">
                                {v2 ? (
                                    <div className="v2-section">
                                        <h3>Select what to migrate</h3>
                                        {analysisResult && <small className="v2-num">· {selectedTrackCount} of {analysisResult.totalTracks}</small>}
                                        {analysisResult && analysisResult.playlists && analysisResult.playlists.length > 0 && !isAnalyzing && (
                                            <button type="button" className="v2-section-link" onClick={toggleSelectAll} disabled={isProcessing}>{allSelected ? 'Deselect all' : 'Select all'}</button>
                                        )}
                                    </div>
                                ) : (
                                    <h3 className="comparison-title">Source Libraries</h3>
                                )}
                                <div className="file-list-container migration-column-content">
                                    {/* Analysis summary cards */}
                                    {!v2 && analysisResult && (
                                        <div className="analysis-summary">
                                            <div className="summary-card">
                                                <div className="summary-value">{analysisResult.totalTracks}</div>
                                                <div className="summary-label">Tracks</div>
                                            </div>
                                            <div className="summary-card">
                                                <div className="summary-value">{analysisResult.totalPlaylists}</div>
                                                <div className="summary-label">Playlists</div>
                                            </div>
                                            <div className="summary-card">
                                                <div className="summary-value">{analysisResult.totalCues || analysisResult.totalCuePoints || 0}</div>
                                                <div className="summary-label">Cues</div>
                                            </div>
                                            <div className="summary-card">
                                                <div className="summary-value">{analysisResult.totalLoops || 0}</div>
                                                <div className="summary-label">Loops</div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Select all / deselect all */}
                                    {!v2 && analysisResult && analysisResult.playlists && analysisResult.playlists.length > 0 && !isAnalyzing && (
                                        <div className="playlist-select-all">
                                            <label className="playlist-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={allSelected}
                                                    onChange={toggleSelectAll}
                                                    disabled={isProcessing}
                                                />
                                                <span className="custom-checkbox" />
                                                <span>Select all</span>
                                            </label>
                                            {selectedTrackCount < (analysisResult.totalTracks || 0) && (
                                                <span className="selection-count">{selectedTrackCount} of {analysisResult.totalTracks} tracks selected</span>
                                            )}
                                        </div>
                                    )}

                                    {/* Playlists with tracks */}
                                    {analysisResult && analysisResult.playlists && analysisResult.playlists.length > 0 && (
                                        <div className="playlist-list">
                                            {analysisResult.playlists.map((playlist, pi) => (
                                                <div key={pi} className={`playlist-section ${!selectedPlaylists.has(playlistKey(playlist, pi)) ? 'playlist-deselected' : ''}`}>
                                                    <div className="playlist-header">
                                                        <label className="playlist-checkbox-label" onClick={e => e.stopPropagation()}>
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedPlaylists.has(playlistKey(playlist, pi))}
                                                                onChange={() => togglePlaylist(playlistKey(playlist, pi))}
                                                                disabled={isProcessing}
                                                            />
                                                            <span className="custom-checkbox" />
                                                        </label>
                                                        <span className="playlist-name">{playlist.name}</span>
                                                        <span className="playlist-count">{playlist.trackCount} tracks</span>
                                                    </div>
                                                    <div className="playlist-tracks">
                                                        {playlist.tracks.map((track, ti) => (
                                                            <div key={ti} className="track-list-entry">
                                                                <div className="track-info">
                                                                    <span className="track-title">{track.title}</span>
                                                                    {track.artist && <span className="track-artist">{track.artist}</span>}
                                                                </div>
                                                                {(track.cueCount > 0 || track.loopCount > 0 || track.cuePointCount > 0) && (
                                                                    <span className="track-cues">
                                                                        {track.cueCount > 0 ? `${track.cueCount} cue${track.cueCount !== 1 ? 's' : ''}` : ''}
                                                                        {track.cueCount > 0 && track.loopCount > 0 ? ', ' : ''}
                                                                        {track.loopCount > 0 ? `${track.loopCount} loop${track.loopCount !== 1 ? 's' : ''}` : ''}
                                                                        {!track.cueCount && !track.loopCount && track.cuePointCount > 0 ? `${track.cuePointCount} cues` : ''}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Tracks not in any playlist */}
                                    {analysisResult && analysisResult.tracks && (() => {
                                        const playlistPaths = new Set();
                                        (analysisResult.playlists || []).forEach(pl =>
                                            pl.tracks.forEach(t => playlistPaths.add(t.filePath))
                                        );
                                        const uncrated = analysisResult.tracks.filter(t => !playlistPaths.has(t.filePath));
                                        if (uncrated.length === 0) return null;
                                        return (
                                            <div className="playlist-list">
                                                <div className={`playlist-section ${!includeUncrated ? 'playlist-deselected' : ''}`}>
                                                    <div className="playlist-header">
                                                        <label className="playlist-checkbox-label" onClick={e => e.stopPropagation()}>
                                                            <input
                                                                type="checkbox"
                                                                checked={includeUncrated}
                                                                onChange={() => setIncludeUncrated(prev => !prev)}
                                                                disabled={isProcessing}
                                                            />
                                                            <span className="custom-checkbox" />
                                                        </label>
                                                        <span className="playlist-name">Tracks not in any crate</span>
                                                        <span className="playlist-count">{uncrated.length} tracks</span>
                                                    </div>
                                                    <div className="playlist-tracks">
                                                        {uncrated.map((track, ti) => (
                                                            <div key={ti} className="track-list-entry">
                                                                <div className="track-info">
                                                                    <span className="track-title">{track.title}</span>
                                                                    {track.artist && <span className="track-artist">{track.artist}</span>}
                                                                </div>
                                                                {(track.cueCount > 0 || track.loopCount > 0 || track.cuePointCount > 0) && (
                                                                    <span className="track-cues">
                                                                        {track.cueCount > 0 ? `${track.cueCount} cue${track.cueCount !== 1 ? 's' : ''}` : ''}
                                                                        {track.cueCount > 0 && track.loopCount > 0 ? ', ' : ''}
                                                                        {track.loopCount > 0 ? `${track.loopCount} loop${track.loopCount !== 1 ? 's' : ''}` : ''}
                                                                        {!track.cueCount && !track.loopCount && track.cuePointCount > 0 ? `${track.cuePointCount} cues` : ''}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* Errors */}
                                    {errors.length > 0 && (
                                        <div className="migration-log-list">
                                            {errors.map((error, index) => (
                                                <div key={`error-${index}`} className="migration-log-entry log-status-error">
                                                    <span className="log-status-icon">{'\u2717'}</span>
                                                    <span className="log-track-name">{error}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Empty / loading states */}
                                    {!analysisResult && !isAnalyzing && errors.length === 0 && (
                                        <div className="empty-log">
                                            {sources.length > 0
                                                ? 'Click Analyze to scan source libraries'
                                                : 'Select source platforms to begin'}
                                        </div>
                                    )}

                                    {isAnalyzing && !analysisResult && (
                                        <div className="empty-log">{analysisStatus || 'Analyzing libraries...'}</div>
                                    )}
                                </div>
                            </div>

                            <div className="comparison-divider"></div>

                            {/* Right column: Migration output/results */}
                            <div className="comparison-column">
                                {v2 ? (
                                    <div className="v2-section">
                                        <h3>{migrationResults ? `Written to ${targetNames || 'target'}` : `Will be written to ${targetNames || '…'}`}</h3>
                                        {migrationLogs.length > 0 ? <small className="v2-num">· {migrationLogs.length}</small> : analysisResult ? <small className="v2-num">· {selectedTrackCount}</small> : null}
                                        {analysisResult && !migrationResults && !isMigrating && <span className="v2-section-link" style={{ cursor: 'default' }}>plan · not yet run</span>}
                                    </div>
                                ) : (
                                    <h3 className="comparison-title">Migration Output</h3>
                                )}
                                <div className="file-list-container migration-column-content">
                                    {/* v2: the plan — what a Migrate would write, before it runs */}
                                    {v2 && analysisResult && migrationLogs.length === 0 && !isMigrating && !migrationResults && (
                                        <div className="track-list v2-plan">
                                            {(() => {
                                                const rows = [];
                                                const seen = new Set();
                                                (analysisResult.playlists || []).forEach((pl, i) => {
                                                    if (!selectedPlaylists.has(playlistKey(pl, i))) return;
                                                    pl.tracks.forEach((t) => { if (!seen.has(t.filePath)) { seen.add(t.filePath); rows.push(t); } });
                                                });
                                                if (includeUncrated) {
                                                    const inPl = new Set();
                                                    (analysisResult.playlists || []).forEach((pl) => pl.tracks.forEach((t) => inPl.add(t.filePath)));
                                                    (analysisResult.tracks || []).forEach((t) => { if (!inPl.has(t.filePath) && !seen.has(t.filePath)) { seen.add(t.filePath); rows.push(t); } });
                                                }
                                                const shown = rows.slice(0, 200);
                                                return (
                                                    <>
                                                        {shown.map((t, i) => (
                                                            <div key={`${i}-${t.filePath}`} className="track-list-entry">
                                                                <span className="v2-dot info" />
                                                                <div className="track-info">
                                                                    <span className="track-title" title={t.filePath}>{t.title || t.filePath}</span>
                                                                    {t.artist && <span className="track-artist">{t.artist}</span>}
                                                                </div>
                                                                {(t.cueCount > 0 || t.loopCount > 0) && (
                                                                    <span className="track-cues">{[t.cueCount > 0 ? `${t.cueCount} cue${t.cueCount !== 1 ? 's' : ''}` : null, t.loopCount > 0 ? `${t.loopCount} loop${t.loopCount !== 1 ? 's' : ''}` : null].filter(Boolean).join(' · ')}</span>
                                                                )}
                                                            </div>
                                                        ))}
                                                        {rows.length > shown.length && <div className="empty-log">… and {rows.length - shown.length} more</div>}
                                                        {rows.length === 0 && <div className="empty-log">Nothing selected</div>}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    )}
                                    {/* Per-track migration results */}
                                    {migrationLogs.length > 0 && (
                                        <div className="track-list">
                                            {migrationLogs.map((log, index) => (
                                                <div
                                                    key={`${index}-${log.filePath}`}
                                                    className={`track-list-entry migrated log-status-${log.status || 'pending'}`}
                                                >
                                                    {v2 ? (
                                                        <span className={`v2-dot ${log.status === 'error' ? 'bad' : log.status === 'warning' ? 'warn' : 'ok'}`} title={log.status || 'migrated'} />
                                                    ) : (
                                                        <span className="track-number">
                                                            <span className={`log-status-icon status-${log.status || 'pending'}`}>
                                                                {getStatusIcon(log.status)}
                                                            </span>
                                                        </span>
                                                    )}
                                                    <div className="track-info">
                                                        <span className="track-title" title={log.filePath}>
                                                            {log.trackName || log.filePath}
                                                        </span>
                                                        {log.artist && <span className="track-artist">{log.artist}</span>}
                                                    </div>
                                                    {log.message && (
                                                        <span className="track-target">{log.message}</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* v2: one green line per run — what landed, and where to open it */}
                                    {v2 && migrationResults && (
                                        <div className={`v2-banner ${migrationResults.success ? 'ok' : 'bad'}`} role="status">
                                            <span>{migrationResults.success ? '✓' : '✗'} <b>{migratedOk}</b> migrated · <b>{migratedSkipped}</b> skipped</span>
                                            {(migrationResults.targetResults || []).filter((r) => r.success && r.outputPath).map((r, i) => (
                                                <button key={i} type="button" className="v2-btn quiet sm" onClick={() => handleOpenOutputFolder(r.outputPath)} title={r.outputPath}>Open {platformName(r.targetPlatform)} ›</button>
                                            ))}
                                        </div>
                                    )}
                                    {/* Migration completion: output paths + instructions */}
                                    {migrationResults && migrationResults.targetResults && migrationResults.targetResults.length > 0 && (
                                        <div className="migration-output-summary">
                                            {migrationResults.targetResults.map((result, i) => (
                                                <div key={i} className={`output-result ${result.success ? 'output-success' : 'output-error'}`}>
                                                    <div className="output-result-header">
                                                        <span className={`output-status-icon ${result.success ? 'status-success' : 'status-error'}`}>
                                                            {result.success ? '\u2713' : '\u2717'}
                                                        </span>
                                                        <span className="output-platform">{result.targetPlatform}</span>
                                                        <span className="output-stats">
                                                            {result.tracksConverted} tracks, {result.playlistsConverted} playlists
                                                        </span>
                                                    </div>
                                                    {result.success && (
                                                        <>
                                                            {result.outputPath && (
                                                                <div className="output-path-row">
                                                                    <span
                                                                        className="output-path"
                                                                        title={result.outputPath}
                                                                        onClick={() => handleOpenOutputFolder(result.outputPath)}
                                                                    >
                                                                        {formatPathDisplay(result.outputPath, 3)}
                                                                    </span>
                                                                    <button
                                                                        className="open-folder-btn"
                                                                        onClick={() => handleOpenOutputFolder(result.outputPath)}
                                                                    >
                                                                        Show in Folder
                                                                    </button>
                                                                </div>
                                                            )}
                                                            <div className={`import-instructions ${result.method === 'database' ? 'direct-import-success' : ''}`}>
                                                                {getImportInstructions(result.targetPlatform, result.method)}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Empty / loading states */}
                                    {migrationLogs.length === 0 && !isMigrating && !migrationResults && !(v2 && analysisResult) && (
                                        <div className="empty-log">
                                            {analysisResult
                                                ? 'Ready to migrate. Click Migrate to start.'
                                                : 'Migration results will appear here'}
                                        </div>
                                    )}

                                    {isMigrating && migrationLogs.length === 0 && (
                                        <div className="empty-log">Migrating...</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MigrateLibraryTab;
